import {
  ApiAccountHistoryReportWithNoteData,
  ApiAccountSnapshot,
  ApiOperationBase,
  ApiProtectedAccountClosureOperation,
  useGetByTenantAccountAndAccountSourceAccountIdentifierQuery,
  useGetByTenantAccountInprogressAndAccountIdentifierQuery
} from 'api/rtk-api'
import clsx from 'clsx'
import { Link } from 'components/core-v2/atoms'
import Icon from 'components/core-v2/atoms/Icon'
import { isSameDay } from 'date-fns'
import { PropsWithChildren } from 'react'
import { ClassName } from 'types/props'
import { formatCurrencyFull } from 'utilities/currency'
import { useUser } from 'utilities/hooks/useUser'
import { getOperationType } from 'utilities/mappers'

import ScalesIcon from '@/assets/scales.svg?react'
import { CURRENCY, PATH, TENANT, TODAY } from '@/const'
import { getUserDisplayInfo, isCollateralUser } from '@/modules/tradebuilder-v2/util/user'

import classes from './index.module.scss'

const getCompletedValue = (closed: boolean, snapshots: ApiAccountSnapshot[]) => {
  if (!closed || snapshots.length === 0) return undefined
  const { operations } = snapshots[snapshots.length - 1]
  const operation = operations.find(
    ({ type }) => type === 'ProtectedAccountClosure'
  ) as ApiProtectedAccountClosureOperation
  return operation?.completedValue
}

export const getInProgressOperation = (data: ApiAccountHistoryReportWithNoteData) => {
  const { account, accountSnapShots } = data
  const { currentlyApproving, currentlyClosing } = account
  const isClosed = account.status === 'Closed'

  const snapshot = accountSnapShots.find(snapshot =>
    isSameDay(TODAY, new Date(snapshot.valuationDate))
  )

  const depositInProgress = snapshot?.operations?.find(op => getOperationType(op) === 'Deposit')
  const rebalancingInProgress = snapshot?.operations?.find(
    op => getOperationType(op) === 'Rebalancing'
  )
  const withdrawalInProgress = snapshot?.operations?.find(
    op => getOperationType(op) === 'Withdrawal'
  )

  return {
    isClosed,
    currentlyClosing,
    currentlyApproving,
    depositInProgress,
    rebalancingInProgress,
    withdrawalInProgress,
    completedValue: getCompletedValue(isClosed, accountSnapShots)
  }
}

type InProgressOperations = ReturnType<typeof getInProgressOperation>

const getAlertLinkOperations = ({
  depositInProgress,
  rebalancingInProgress,
  withdrawalInProgress
}: InProgressOperations) => {
  const operations: { op: ApiOperationBase; text: string }[] = []
  if (depositInProgress) operations.push({ text: 'Deposit', op: depositInProgress })
  if (rebalancingInProgress) operations.push({ text: 'Rebalancing', op: rebalancingInProgress })
  if (withdrawalInProgress) operations.push({ text: 'Withdrawal', op: withdrawalInProgress })
  return operations
}

export interface TradeStatusAlertProps extends ClassName {
  tradeId: string
  tradeSource: string
}

type AlertLinkProps = {
  operations: { op: ApiOperationBase; text: string }[]
  source: string
  tradeId: string
}

const AlertLinks = ({ operations, source, tradeId }: AlertLinkProps) => (
  <>
    {operations.map(({ op, text }, index) => (
      <>
        <Link
          to={PATH.reportsSingleTradeEventsEvent(source, tradeId, TODAY, op.operationId)}
          variant="link"
        >
          <ScalesIcon />
          {text} in progress
        </Link>
        {index < operations.length - 1 && <div className={classes.verticalRule} />}
      </>
    ))}
  </>
)

type AlertWrapperProps = PropsWithChildren<{ lock?: boolean }> & ClassName

export const AlertWrapper = ({ children, className, lock }: AlertWrapperProps) => {
  if (!lock) return <div className={clsx(classes.root, className)}>{children}</div>

  return (
    <div className={clsx(classes.root, className)}>
      <div className={classes.inner}>
        <Icon name="padlock" />
        {children}
      </div>
    </div>
  )
}

const Alert = ({ className, tradeId, tradeSource }: TradeStatusAlertProps) => {
  const { data: user } = useUser()
  const { userRoles } = getUserDisplayInfo(user)

  const { data, isLoading } = useGetByTenantAccountAndAccountSourceAccountIdentifierQuery({
    tenant: TENANT,
    accountIdentifier: tradeId,
    accountSource: tradeSource
  })

  const { data: tradingAccount, isLoading: isLoadingTradingAccount } =
    useGetByTenantAccountInprogressAndAccountIdentifierQuery({
      tenant: TENANT,
      accountIdentifier: tradeId
    })

  if (isLoading || isLoadingTradingAccount) return null

  const inProgress = getInProgressOperation(data)
  const { completedValue, currentlyApproving, currentlyClosing, isClosed } = inProgress

  if (isClosed) {
    const value = formatCurrencyFull(completedValue, CURRENCY)
    return (
      <AlertWrapper className={className}>
        <span>This trade is closed. Its redemption value on closing was {value}</span>
      </AlertWrapper>
    )
  }
  if (currentlyClosing) {
    return (
      <AlertWrapper className={className}>
        <span>This trade is closing.</span>
      </AlertWrapper>
    )
  }
  if (tradingAccount.note.isCollateralForLoan && !isCollateralUser(userRoles)) {
    return (
      <AlertWrapper lock className={className}>
        <span>
          This trade is locked for updates, because the note is used as collateral for a loan.
        </span>
      </AlertWrapper>
    )
  }
  if (currentlyApproving) {
    return (
      <AlertWrapper lock className={className}>
        <span className="mr-auto">
          This trade is locked for updates, because it has instructions awaiting approval.
        </span>
        <AlertLinks
          operations={getAlertLinkOperations(inProgress)}
          source={tradeSource}
          tradeId={tradeId}
        />
      </AlertWrapper>
    )
  }
  if (tradingAccount.isLockedForUpdates) {
    return (
      <AlertWrapper lock className={className}>
        <span>
          This trade is locked for updates, because it's the originating trade of a switch.
        </span>
      </AlertWrapper>
    )
  }

  return null
}

export const TradeStatusAlert = ({ tradeSource, ...rest }: TradeStatusAlertProps) =>
  tradeSource.toLowerCase() === 'internal' ? <Alert tradeSource={tradeSource} {...rest} /> : null
